import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { heIL } from '@mui/x-data-grid/locales';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import ErrorAlert from '../components/Common/ErrorAlert';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { useDirection } from '../context/DirectionContext';
import { translate as tr, translateOr } from '../i18n/translations';
import apiService from '../services/api';
import { CreateUserDto, User, UserRole } from '../types/api';
import { getErrorMessage } from '../utils/errorUtils';

const roleColor = (role: UserRole) => {
  const map: Record<UserRole, 'primary' | 'success' | 'info' | 'warning' | 'default'> = {
    [UserRole.ADMIN]: 'primary',
    [UserRole.TEAMLEAD]: 'primary',
    [UserRole.ASSESSOR]: 'success',
    [UserRole.SOCIO]: 'info',
    [UserRole.INTERVIEWER]: 'warning',
    [UserRole.VIEWER]: 'default',
  };
  return map[role] ?? 'default';
};

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': { bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#64748B',
  },
  '& .MuiDataGrid-row:hover': { bgcolor: '#F8FAFC' },
  '& .MuiDataGrid-cell': { borderBottom: '1px solid #F1F5F9', fontSize: '0.875rem', color: '#0F172A' },
  '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #E2E8F0' },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
};

const Users: React.FC = () => {
  const queryClient = useQueryClient();
  const { lang } = useDirection();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<CreateUserDto>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roles: [],
  });

  const { data: users, isLoading, error: queryError } = useQuery({
    queryKey: ['users'],
    queryFn: () => apiService.getUsers(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateUserDto) => apiService.createUser(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); closeDialog(); },
    onError: (e: any) => setError(getErrorMessage(e)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<CreateUserDto>) =>
      apiService.updateUser(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); closeDialog(); },
    onError: (e: any) => setError(getErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
    onError: (e: any) => setError(getErrorMessage(e)),
  });

  const openDialog = (user?: User) => {
    setEditingUser(user ?? null);
    setFormData(
      user
        ? { email: user.email, firstName: user.firstName, lastName: user.lastName, password: '', roles: user.roles ?? [] }
        : { email: '', firstName: '', lastName: '', password: '', roles: [] }
    );
    setError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setError('');
  };

  const handleSubmit = () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError(tr('users.fillRequired', lang));
      return;
    }
    if (!editingUser && !formData.password) {
      setError(tr('users.passwordReqMsg', lang));
      return;
    }
    if (editingUser) {
      const { password, ...rest } = formData;
      updateMutation.mutate(password ? { id: editingUser.id, ...formData } : { id: editingUser.id, ...rest });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string, isAdmin: boolean) => {
    if (isAdmin) return;
    if (window.confirm(tr('users.confirmDelete', lang))) deleteMutation.mutate(id);
  };

  const handleRolesChange = (event: SelectChangeEvent<UserRole[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      roles: typeof value === 'string' ? (value.split(',') as UserRole[]) : value,
    }));
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: tr('users.col.name', lang),
      flex: 1,
      minWidth: 160,
      renderCell: (p: GridRenderCellParams) => (
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.3 }}>
            {p.row.firstName} {p.row.lastName}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>{p.row.email}</Typography>
        </Box>
      ),
    },
    {
      field: 'roles',
      headerName: tr('users.col.roles', lang),
      width: 220,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(p.value as UserRole[])?.map(r => (
            <Chip key={r} label={translateOr(`role.${r}`, lang, r)} size="small" color={roleColor(r)} variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      field: 'isActive',
      headerName: tr('users.col.status', lang),
      width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={p.value ? tr('common.active', lang) : tr('common.inactive', lang)}
          size="small"
          color={p.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: tr('users.col.created', lang),
      width: 120,
      renderCell: (p: GridRenderCellParams) => (
        <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
          {new Date(p.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 80,
      getActions: ({ id, row }) => [
        <GridActionsCellItem
          icon={<EditIcon fontSize="small" />}
          label={tr('common.edit', lang)}
          onClick={() => openDialog(row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon fontSize="small" />}
          label={tr('common.delete', lang)}
          disabled={row.isAdmin}
          onClick={() => handleDelete(id as string, row.isAdmin)}
          sx={{ color: '#EF4444' }}
        />,
      ],
    },
  ];

  if (isLoading) return <LoadingSpinner message={tr('users.loading', lang)} />;
  if (queryError) return <ErrorAlert error={queryError} title={tr('users.errorLoad', lang)} />;

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
            {tr('common.mgmt', lang)}
          </Typography>
          <Typography variant="h5">
            {tr('nav.users', lang)}
            <Typography component="span" sx={{ marginInlineStart: '12px', fontSize: '0.9rem', fontWeight: 400, color: '#94A3B8' }}>
              {users?.length ?? 0}
            </Typography>
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>
          {tr('users.addUser', lang)}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', borderRadius: 3, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
        <DataGrid
          localeText={lang === 'he' ? heIL.components.MuiDataGrid.defaultProps.localeText : undefined}
          rows={users ?? []}
          columns={columns}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
          rowHeight={58}
          sx={gridSx}
        />
      </Paper>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? tr('users.editUser', lang) : tr('users.addUser', lang)}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <TextField
              autoFocus
              label={tr('users.firstName', lang)}
              value={formData.firstName}
              onChange={e => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label={tr('users.lastName', lang)}
              value={formData.lastName}
              onChange={e => setFormData({ ...formData, lastName: e.target.value })}
            />
          </Box>
          <TextField
            label={tr('users.emailReq', lang)}
            type="email"
            fullWidth
            value={formData.email}
            onChange={e => setFormData({ ...formData, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label={editingUser ? tr('users.passwordEdit', lang) : tr('users.passwordReq', lang)}
            type="password"
            fullWidth
            value={formData.password}
            onChange={e => setFormData({ ...formData, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>{tr('common.roles', lang)}</InputLabel>
            <Select
              multiple
              value={formData.roles ?? []}
              onChange={handleRolesChange}
              input={<OutlinedInput label={tr('common.roles', lang)} />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as UserRole[]).map(r => (
                    <Chip key={r} label={translateOr(`role.${r}`, lang, r)} size="small" color={roleColor(r)} />
                  ))}
                </Box>
              )}
            >
              {Object.values(UserRole).map(r => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDialog} color="inherit">
            {tr('common.cancel', lang)}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingUser ? tr('common.save', lang) : tr('users.createUser', lang)}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default Users;
